'use client';
import Typography from '@/components/shared/typography/typography.component';
import Image from 'next/image';
import {
    CalendarBookedCar,
    NavigatorIcon,
    PhoneCallIcon,
    PlusGreenIcon,
    BookingsBlackMarker,
} from 'public/Images';
import ControllerButtons from './add-payments-button.component';
import CardTag from './card-tag.component';
import { FC, useState } from 'react';
import { useWindowDimensions, useTranslation } from '@/hook';
import {
    BookingButtonTypes,
    BookingTypes,
    CardStatusTypes,
    ReserveTypes,
} from '@/types';
import SylndrDirectionsModal from './directionsModal.component';
import SylndrDirectionsBottomSheet from './directionBottomSheet.component';
import SylndrCallsBottomSheet from './callsBottomSheet.component';
import { useRouter } from 'next/navigation';
import myImageLoader from '@/image.local.loader';
import { useDispatch } from 'react-redux';
import { setRedirectedFromAddPaymentButton } from '../../../../../redux/reducers/bookings-for-payment-logs-reducer';
import useAuth from '@/hook/use-auth.hook';
import { FEATURE_FLAG_DIGITALIZATION_RELEASE_3 } from '@/constants';
import { digitalizationWhiteList } from '@/constants/digitalization-whitelist';
interface BookingCardProps {
    vehicleName: string;
    vehicleImage: string;
    vehicleTransmission: string;
    vehicleKm: string;
    carDetailsLink: string;
    bookingDate: string;
    reservationTitle: string;
    vehiclePrice: string;
    bookingLocation: string;
    cardStatus: CardStatusTypes;
    reserveType: ReserveTypes;
    bookingType: BookingTypes;
    cardIsClickable: boolean;
    showAddPaymentButton: boolean;
    showControllers: boolean;
    id: string;
    cantBeClickedByDefault: null | true;
}

const BookingCard: FC<BookingCardProps> = ({
    vehicleName,
    vehicleImage,
    vehicleTransmission,
    vehicleKm,
    bookingDate,
    reservationTitle,
    bookingLocation,
    cardStatus,
    reserveType,
    bookingType,
    showAddPaymentButton,
    showControllers,
    cardIsClickable,
    cantBeClickedByDefault,
    id,
}) => {
    const { userId } = useAuth();
    const whiteListedUser = digitalizationWhiteList.includes(userId);

    const canAccessRelease3 =
        FEATURE_FLAG_DIGITALIZATION_RELEASE_3 || whiteListedUser;

    const { IS_DESKTOP } = useWindowDimensions();
    const { t, i18n } = useTranslation();
    const router = useRouter();
    const dispatch = useDispatch();
    const [openDirectionsModal, setOpenDirectionsModal] = useState(false);
    const [openCallsModal, setOpenCallsModal] = useState(false);

    const openDirectionsModalHandler = (state: boolean) => {
        setOpenDirectionsModal(state);
    };
    const openCallsModalHandler = (state: boolean) => {
        setOpenCallsModal(state);
    };
    const enableClickingBasedOnData =
        canAccessRelease3 && !cantBeClickedByDefault && cardIsClickable;
    return (
        <div
            className={`p-3 md:h-auto max-h-[170px] ${
                cantBeClickedByDefault ? '' : ' md:min-h-[170px]'
            } border border-gray-200 rounded-lg flex flex-col gap-2  justify-start ${
                enableClickingBasedOnData && 'cursor-pointer'
            }`}
            onClick={() => {
                enableClickingBasedOnData &&
                    router.push(
                        `/${i18n.language}/account/mybookings/${id}/payments`
                    );
                dispatch(setRedirectedFromAddPaymentButton(false));
            }}
        >
            <div className="flex items-center justify-between pb-2 border-b-[1px] border-gray-200">
                <div className="flex items-center gap-1">
                    <Image width={12} src={CalendarBookedCar} alt="calendar" />
                    <Typography
                        variant="caption"
                        className="text-gray-800 !text-[10px] translate-y-[1px]"
                    >
                        {bookingDate}
                    </Typography>
                </div>
                <div className="flex items-center gap-1">
                    <Image
                        width={14}
                        src={BookingsBlackMarker}
                        alt="calendar"
                    />
                    <Typography
                        variant="caption"
                        className="text-gray-800 !text-[10px] ar:translate-y-[1px]"
                    >
                        {reservationTitle}
                    </Typography>
                </div>
            </div>
            <div className="flex justify-start items-center ">
                <img
                    src={myImageLoader({
                        src: vehicleImage,
                        quality: 75,
                        width: 200,
                        height: 200,
                    })}
                    alt="vehicle"
                    className=" bg-gray-400 w-24 h-[69px] rounded-lg en:mr-3 ar:ml-3"
                />

                <div className="flex flex-col gap-1 md:w-9/12 w-[70%] ">
                    <div className="flex gap-1.5">
                        <Typography
                            variant="captionBold"
                            className="text-gray-700"
                        >
                            {vehicleName}
                        </Typography>
                        <div className="en:ml-auto en:mr-0 ar:ml-0 ar:mr-auto">
                            <CardTag type={cardStatus} />
                        </div>
                    </div>
                    <div className="flex items-center justify-start gap-2 w-max">
                        <Typography variant="caption" className="text-gray-500">
                            {`${vehicleKm} ${t('km')}`}
                        </Typography>
                        <div className="w-0.5 h-0.5 bg-gray-500" />
                        <Typography variant="caption" className="text-gray-500">
                            {vehicleTransmission}
                        </Typography>
                    </div>
                </div>
            </div>

            {!cantBeClickedByDefault &&
                showControllers &&
                !showAddPaymentButton && (
                    <>
                        <div className="flex justify-between items-center gap-2">
                            <ControllerButtons
                                type={BookingButtonTypes.Directions}
                                text="Directions"
                                icon={NavigatorIcon}
                                onClick={() => {
                                    openDirectionsModalHandler(true);
                                }}
                            />
                            {!IS_DESKTOP && (
                                <ControllerButtons
                                    type={BookingButtonTypes.Call}
                                    text="Call"
                                    icon={PhoneCallIcon}
                                    onClick={() => {
                                        openCallsModalHandler(true);
                                    }}
                                />
                            )}
                        </div>
                        {IS_DESKTOP ? (
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                }}
                            >
                                <SylndrDirectionsModal
                                    open={openDirectionsModal}
                                    closeModal={() => {
                                        openDirectionsModalHandler(false);
                                    }}
                                    title={reservationTitle}
                                    bookingLocation={bookingLocation}
                                    reserveType={reserveType}
                                    bookingType={bookingType}
                                />
                            </div>
                        ) : (
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                }}
                            >
                                <SylndrDirectionsBottomSheet
                                    open={openDirectionsModal}
                                    closeModal={() => {
                                        openDirectionsModalHandler(false);
                                    }}
                                    title={reservationTitle}
                                    bookingLocation={bookingLocation}
                                    reserveType={reserveType}
                                    bookingType={bookingType}
                                />
                                <SylndrCallsBottomSheet
                                    open={openCallsModal}
                                    closeModal={() => {
                                        openCallsModalHandler(false);
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            {canAccessRelease3 &&
                !cantBeClickedByDefault &&
                showAddPaymentButton && (
                    <ControllerButtons
                        type={BookingButtonTypes.Payments}
                        text={t('addPayments')}
                        icon={PlusGreenIcon}
                        onClick={() => {
                            dispatch(setRedirectedFromAddPaymentButton(true));
                            router.push(
                                `/${i18n.language}/account/mybookings/${id}/payments`
                            );
                        }}
                    />
                )}
        </div>
    );
};

export default BookingCard;
